import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, User, LogIn, LogOut, Menu, HomeIcon, Info, Mail, Package } from 'lucide-react';
import { Button } from './ui/Button'; // Assuming Button is in ./ui/Button relative to components
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.png'; // Assuming logo is in ../assets/
import { Link, useNavigate } from 'react-router-dom'; // Import Link and useNavigate

// Props definition for NavBar (onNavigate is removed)
// interface NavBarProps {
//   // onNavigate: (page: 'home' | 'admin' | 'about' | 'contact' | 'products' | 'login') | string) => void;
// }

export const NavBar: React.FC = () => { // Removed onNavigate from props
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate(); // Initialize useNavigate
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false); // Close mobile menu if switching to desktop
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogoutAndRedirect = async () => {
    await logout();
    navigate('/'); // Redirect to home page after logout
    setIsMobileMenuOpen(false);
  };

  // Helper to close mobile menu on link click
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <nav className="navbar">
      <div className="navbar-left">
        {isMobile && (
          <button className="menu-button" onClick={toggleMobileMenu} aria-label="Open menu">
            <Menu className="icon" />
          </button>
        )}
        <Link to="/" className="navbar-brand" onClick={closeMobileMenu}>
          <img src={logo} alt="Venus HVAC Supplies Logo" className="navbar-logo w-8 h-8 object-contain" />
        </Link>
      </div>

      {/* Desktop Menu */}
      {!isMobile && (
        <div className="navbar-desktop-menu">
          <Link to="/" className="nav-link"><HomeIcon className="icon-inline" /> Home</Link>
          <Link to="/products" className="nav-link"><Package className="icon-inline" /> Products</Link>
          <Link to="/about" className="nav-link"><Info className="icon-inline" /> About Us</Link>
          <Link to="/contact" className="nav-link"><Mail className="icon-inline" /> Contact Us</Link>
          {isAuthenticated ? (
            <>
              <Link to="/admin" className="nav-link"><LogIn className="icon-inline" /> Admin</Link>
              <Button variant="outline" onClick={handleLogoutAndRedirect} className="logout-button">
                <LogOut className="icon-inline" /> Logout
              </Button>
            </>
          ) : (
            <Link to="/login" className="nav-link"><LogIn className="icon-inline" /> Login</Link>
          )}
          <Link to="/cart" className="nav-link"><ShoppingCart className="icon" /></Link>
          <Link to="/profile" className="nav-link"><User className="icon" /></Link>
        </div>
      )}

      {/* Mobile Menu */}
      {isMobile && (
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="navbar-mobile-menu"
            >
              <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>Home</Link>
              <Link to="/products" className="mobile-nav-link" onClick={closeMobileMenu}>Products</Link>
              <Link to="/about" className="mobile-nav-link" onClick={closeMobileMenu}>About Us</Link>
              <Link to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>Contact Us</Link>
              {isAuthenticated ? (
                <>
                  <Link to="/admin" className="mobile-nav-link" onClick={closeMobileMenu}>Admin</Link>
                  <Button variant="outline" onClick={handleLogoutAndRedirect} className="mobile-nav-link">Logout</Button>
                </>
              ) : (
                <Link to="/login" className="mobile-nav-link" onClick={closeMobileMenu}>Login</Link>
              )}
              <Link to="/cart" className="mobile-nav-link" onClick={closeMobileMenu}>Cart</Link>
              <Link to="/profile" className="mobile-nav-link" onClick={closeMobileMenu}>Profile</Link>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </nav>
  );
};

// Default export if it's the main export of the file
export default NavBar; // Changed to default export for consistency if it's the sole export
